
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { ClaudeAgent } from './claudeagents.types';

const SEED_AGENTS: ClaudeAgent[] = [
  {
    id: 'agent-001',
    name: 'AMS Copilot',
    description: 'Intelligent AMS assistant that searches policies, retrieves client data, and generates professional coverage summaries on demand.',
    model: 'claude-sonnet-4-6',
    status: 'active',
    instructions: 'You are an AMS Copilot for an independent insurance agency. Help agents quickly find policy information, client details, and generate professional summaries. Always be accurate and cite specific policy numbers when available.',
    skills: [
      { id: 's1', name: 'AMS Search',        description: 'Search and retrieve data from the AMS', category: 'Data',      icon: '🔍', enabled: true  },
      { id: 's2', name: 'Policy Summary',    description: 'Generate concise policy summaries',      category: 'Generator', icon: '📄', enabled: true  },
      { id: 's3', name: 'Coverage Analysis', description: 'Analyze coverage gaps',                  category: 'Analysis',  icon: '🛡️', enabled: false },
    ],
    mcpServers: [
      { id: 'm1', name: 'AMS Connect',    endpoint: 'mcp://ams.local/v1',  description: 'Direct AMS database connection', status: 'connected',    tools: ['search_policies','get_client','update_notes'] },
      { id: 'm2', name: 'Document Store', endpoint: 'mcp://docs.local/v1', description: 'Agency document repository',      status: 'disconnected', tools: ['upload_doc','fetch_doc'] },
    ],
    createdAt: '2026-04-10T09:00:00Z', updatedAt: '2026-05-01T14:30:00Z',
    messageCount: 1284, lastActive: '2026-05-11T08:22:00Z',
    color: '#8b5cf6', temperature: 0.3, maxTokens: 4096,
  },
  {
    id: 'agent-002',
    name: 'Renewal Advisor',
    description: 'Proactively identifies renewal opportunities, drafts personalized outreach emails, and flags coverage gaps before policy expiration.',
    model: 'claude-sonnet-4-6',
    status: 'active',
    instructions: 'You are a Renewal Advisor specializing in proactive policy renewals for insurance agencies. Identify clients with upcoming renewals, assess their current coverage, and draft personalized outreach to maximize retention and cross-sell opportunities.',
    skills: [
      { id: 's4', name: 'Renewal Scanner',     description: 'Identify upcoming policy renewals',  category: 'Scanner',   icon: '🔄', enabled: true },
      { id: 's5', name: 'Email Drafting',      description: 'Write personalized renewal emails',  category: 'Generator', icon: '✉️', enabled: true },
      { id: 's6', name: 'Cross-sell Analysis', description: 'Identify cross-sell opportunities',  category: 'Analysis',  icon: '📊', enabled: true },
    ],
    mcpServers: [
      { id: 'm3', name: 'AMS Connect',   endpoint: 'mcp://ams.local/v1',   description: 'Direct AMS database connection', status: 'connected', tools: ['search_policies','get_renewals'] },
      { id: 'm4', name: 'Email Service', endpoint: 'mcp://email.local/v1', description: 'Agency email service',            status: 'connected', tools: ['send_email','draft_email'] },
    ],
    createdAt: '2026-04-15T10:00:00Z', updatedAt: '2026-05-08T16:00:00Z',
    messageCount: 847, lastActive: '2026-05-10T15:45:00Z',
    color: '#f97316', temperature: 0.5, maxTokens: 8192,
  },
  {
    id: 'agent-003',
    name: 'Claims Assistant',
    description: 'Streamlines claims intake, documentation collection, and status tracking. Reduces claims processing time and improves client communication.',
    model: 'claude-haiku-5-5',
    status: 'inactive',
    instructions: 'You are a Claims Assistant for an independent insurance agency. Help agents and clients with claims intake, documentation requirements, and status updates. Be empathetic, clear, and efficient in your communication.',
    skills: [
      { id: 's7', name: 'Claims Intake',      description: 'Guide through claims intake process',    category: 'Process',   icon: '📋', enabled: true  },
      { id: 's8', name: 'Document Checklist', description: 'Generate required document checklists',  category: 'Generator', icon: '✅', enabled: true  },
      { id: 's9', name: 'Status Tracker',     description: 'Track and update claims status',         category: 'Tracker',   icon: '📍', enabled: false },
    ],
    mcpServers: [
      { id: 'm5', name: 'Claims Portal', endpoint: 'mcp://claims.local/v1', description: 'Carrier claims portal integration', status: 'error', tools: ['submit_claim','get_status'] },
    ],
    createdAt: '2026-04-20T11:00:00Z', updatedAt: '2026-04-28T09:00:00Z',
    messageCount: 312, lastActive: '2026-04-28T09:00:00Z',
    color: '#22d3ee', temperature: 0.2, maxTokens: 2048,
  },
];

/* ── Context shape ── */
interface ClaudeAgentsContextValue {
  agents: ClaudeAgent[];
  getAgentById: (id: string) => ClaudeAgent | undefined;
  addAgent: (agent: ClaudeAgent) => void;
  updateAgent: (agent: ClaudeAgent) => void;
}

const ClaudeAgentsContext = createContext<ClaudeAgentsContextValue | null>(null);

/* ── Provider ── */
export const ClaudeAgentsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [agents, setAgents] = useState<ClaudeAgent[]>(SEED_AGENTS);
  // TODO (API): replace the line above with:
  //   const [agents, setAgents] = useState<ClaudeAgent[]>([]);
  //   useEffect(() => { fetchAgentsFromAPI().then(setAgents); }, []);

  /* Sync agents created via CreateAgentPanel's custom event */
  useEffect(() => {
    const handler = (e: Event) => {
      const agent = (e as CustomEvent<ClaudeAgent>).detail;
      setAgents(prev => [agent, ...prev]);
    };
    window.addEventListener('agentCreated', handler);
    return () => window.removeEventListener('agentCreated', handler);
  }, []);

  const getAgentById = useCallback(
    (id: string) => agents.find(a => a.id === id),
    [agents],
  );

  const addAgent = useCallback((agent: ClaudeAgent) => {
    setAgents(prev => [agent, ...prev]);
  }, []);

  const updateAgent = useCallback((updated: ClaudeAgent) => {
    setAgents(prev => prev.map(a => (a.id === updated.id ? updated : a)));
  }, []);

  return (
    <ClaudeAgentsContext.Provider value={{ agents, getAgentById, addAgent, updateAgent }}>
      {children}
    </ClaudeAgentsContext.Provider>
  );
};

/* ── Hook ── */
export const useClaudeAgents = (): ClaudeAgentsContextValue => {
  const ctx = useContext(ClaudeAgentsContext);
  if (!ctx) throw new Error('useClaudeAgents must be used inside <ClaudeAgentsProvider>');
  return ctx;
};
